import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import DownloadLabel from '../../components/common/DownloadLabel/Download';
import ExportLabel from '../../components/common/ExportLabel/Export';
import InputBoxLarge from '../../components/common/InputBox/InputBoxLarge';  
import './AddRecipient.css';
import '../../components/common/SubNavbar.css';
import '../../components/common/CommonText.css';
import { baseurl } from '../../api';
import { isNumberAndTextOnly, isRequired } from '../../validation';
import authenticatedAxios from '../../authenticatedAxios';
import { showAlert } from '../../components/common/ExportLabel/Exporterror';


const EditRecipient = () => {
  const location = useLocation();
  const { item } = location.state || {};
  const [formValues, setFormValues] = useState({ name: item?.name || '' });
  const [activeButton, setActiveButton] = useState('Companies');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectAllCompanies, setSelectAllCompanies] = useState(false);
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (item) {
      fetchInitialData(item.recipients);
    }
  }, [item]);

  const fetchInitialData = async (recipients) => {
    setIsLoading(true);
    try {
      const [usersResponse, companiesResponse] = await Promise.all([
        authenticatedAxios.get(`${baseurl}accounts/users/organizational/active/`),
        authenticatedAxios.get(`${baseurl}organization/`),
      ]);

      const usersData = usersResponse.data.data.map((user) => ({
        ...user,
        companyName: user.organization?.name || 'No Company',
      }));

      const companiesData = companiesResponse.data.data;

      setAllUsers(usersData);
      setAllCompanies(companiesData);

      const preselectedUsers = usersData.filter((user) => recipients.includes(user.id));
      setSelectedUsers(preselectedUsers);
      
      const preselectedCompanyIds = [...new Set(preselectedUsers.map((user) => user.organization?.id))];
      const preselectedCompanies = companiesData.filter((company) => preselectedCompanyIds.includes(company.id));
      setSelectedCompanies(preselectedCompanies);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleButtonClick = (buttonType) => {
    setActiveButton(buttonType);
  };

  const handleSearchChange = (e, type) => {
    const value = e.target.value;
    if (type === 'Companies') {
      setCompanySearch(value);
    } else {
      setUserSearch(value);
    }
  };

  const filteredCompanies = allCompanies.filter((company) =>
    company.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const filteredUsers = allUsers.filter((user) =>
    user.full_name.toLowerCase().includes(userSearch.toLowerCase())
  );
  const handleCompanySelection = (companyId) => {
    const isSelected = selectedCompanies.some((company) => company.id === companyId);
    const companyUsers = allUsers.filter((user) => user.organization?.id === companyId);

    if (isSelected) {
      setSelectedCompanies((prev) => prev.filter((company) => company.id !== companyId));
      setSelectedUsers((prev) => prev.filter((user) => user.organization?.id !== companyId));
    } else {
      setSelectedCompanies((prev) => [
        ...prev, 
        allCompanies.find((company) => company.id === companyId),
      ]);
      setSelectedUsers((prev) => [
        ...prev, 
        ...companyUsers,
      ]);
    }
  };

  const handleUserSelection = (userId) => {
    const isSelected = selectedUsers.some((user) => user.id === userId);

    if (isSelected) {
      setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
    } else {
      setSelectedUsers((prev) => [...prev, allUsers.find((user) => user.id === userId)]);
    }
  };

  const handleSaveToGroup = async () => {
    if (!selectedUsers || selectedUsers.length === 0) {
      showAlert('Please select at least one recipient before saving.');
      return;
    }
  
    try {
      setIsLoading(true);
      const updatedRecipients = selectedUsers.map((user) => user.id);
  
      const payload = {
        name: formValues.name,
        recipients: updatedRecipients,
      };
  
      const response = await authenticatedAxios.put(
        `${baseurl}notifications/recipient-groups/?id=${item.id}`,
        payload
      );
  
      navigate('/recipient-groups');
  
    } catch (error) {
      console.error('Error updating group:', error);
  
      if (error.response) {
        showAlert(`Failed to update group: ${error.response.data.message || 'Unknown error'}`);
      } else {
        showAlert('Failed to update group. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleSelectAllCompanies = () => {
    if (!selectedCompanies || selectedUsers.length === 0) {
      showAlert('Please select at least one recipient before saving.');
      return;
    }
  
    if (selectAllCompanies) {
      setSelectedCompanies([]);
      setSelectedUsers([]);
    } else {
      setSelectedCompanies(allCompanies);
      const allCompanyUsers = allUsers.filter((user) =>
        allCompanies.some((company) => user.organization?.id === company.id)
      );
      setSelectedUsers(allCompanyUsers);
    }
    setSelectAllCompanies(!selectAllCompanies);
  };
  
  const handleSelectAllUsers = () => {
    if (!selectedUsers || selectedUsers.length === 0) {
      showAlert('Please select at least one recipient before saving.');
      return;
    }
  
    if (selectAllUsers) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allUsers);
    }
    setSelectAllUsers(!selectAllUsers);
  };
  
  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Notification/Recipient Groups"
          nestedpageName={`Edit ${item?.name}`}
          linkTo="/recipient-groups"
        />
        <div className="export-container">
          <DownloadLabel />
          <ExportLabel />
        </div>
      </div>
      <div className="recipient-top-container add-input-box-container">
        <h1 className="fts-18 black common-text ftw-5">Recipient Group</h1>
        <button
          className="popup-add-button white fts-15"
          onClick={handleSaveToGroup}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save To Group'}
        </button>
        <InputBoxLarge
          label="Group Name"
          name="name"
          defaultValue={formValues.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
        />
      </div>
      {/* Recipient Selection */}
      <div className="recipient-top-container">
        <div className="recipient-heading">
          <h1 className="fts-18 black common-text ftw-5">Recipients List</h1>
        </div>
        <div className="d-flex justify-b-w mb-4">
          <div className="buttons-recipient">
          <a
            className={`common-text fts-15 fts-4 black ${activeButton === 'Companies' ? 'active' : ''}`}
            onClick={() => handleButtonClick('Companies')}
          >
            Companies
          </a>
          <a
            className={`common-text fts-15 fts-4 black ${activeButton === 'Users' ? 'active' : ''}`}
            onClick={() => handleButtonClick('Users')}
          >
            User Accounts
          </a>
          </div>
          <span className="text-right common-text fts-15 fts-4 black">
            Total recipients: {selectedUsers.length}
          </span>
        </div>
          <div className="search-recipient-container">
            <input
              className="search-recipient"
              placeholder={`Search ${activeButton}`}
              onChange={(e) => handleSearchChange(e, activeButton)}
            />
            <svg
              className="search-recipient-icon recipient-search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
            </svg>
          </div>
        {/* selected companies */}
        {activeButton === 'Companies' && (
          <div className="table-container-recipient" id="company-option">
            <table className="compnies-popup common-table">
              <thead className="recipient-common-table-head">
                <tr className="recipient-common-table-thead-tr">
                  <th className="company-name-th left-radius">Company Name</th>
                  <th className='right-radius'>
                    <input
                      className="table-selectbox table-parent-selectbox"
                      type="checkbox"
                      name="selectAllCompanies"
                      onChange={handleSelectAllCompanies}
                      checked={selectAllCompanies}
                    />
                  </th>
                </tr>
              </thead>                          
              <tbody className="recipient-common-table-tbody">
                {filteredCompanies.map((company) => (
                <tr className="recipient-common-table-body-tr" key={company.id}>
                  <td className="company-name-td">{company.name}</td>
                  <td>
                  <input
                    className="table-selectbox table-child-selectbox" type="checkbox" name="language" checked={selectedCompanies.some(
                      (selected) => selected.id === company.id
                    )}
                    onChange={() => handleCompanySelection(company.id)}
                  />
                  </td>
                </tr>
              ))}
                <tr> </tr>                           
              </tbody>
              </table>
          </div>
        )}
        {/* selected users */}
        {activeButton === 'Users' && (
          <div className="table-container-recipient" id="user-accounts-option">
            <table className="users-popup common-table">
              <thead className="recipient-common-table-head">
                <tr className="recipient-common-table-thead-tr">
                  <th className="user-name-th left-radius">User NAME</th>
                  <th className="user-name-th">Company NAME</th>
                  <th className='right-radius'>
                    <input
                      className="table-selectbox table-parent-selectbox"
                      type="checkbox"
                      name="selectAllUsers"
                      onChange={handleSelectAllUsers}
                      checked={selectAllUsers}
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="recipient-common-table-tbody">
                {filteredUsers.map((user) => (
                  <tr className="recipient-common-table-body-tr" key={user.id}>
                    <td className="user-name-td">{user.full_name}</td>
                    <td className="user-name-td">{user.organization?.name || 'Unknown Company'}</td> 
                    <td>
                      <input
                        className="table-selectbox table-child-selectbox"
                        type="checkbox"
                        name="language"
                        checked={selectedUsers.some(
                          (selected) => selected.id === user.id
                        )}
                        onChange={() => handleUserSelection(user.id)}
                      />                                              
                    </td>
                  </tr>
                ))}
                <tr>                    
                </tr>                  
              </tbody>
            </table>
          </div>
      )}
      </div>
    </div>
  );
};

export default EditRecipient;
